/**
 * Error Report Service - Client-side error reporting to backend API
 *
 * Features:
 * - Batches errors (sends every 30s or 10 errors, whichever comes first)
 * - Queues errors in localStorage for offline support
 * - Adds browser context (userAgent, memory, screen)
 * - Adds app context (symbol, indicators, connection status)
 * - Desktop state capture for error reproduction
 * - Exponential backoff on failure
 * - Privacy mode (sanitizes sensitive data)
 */

import { settings, parsedMarketdataUrls } from "../data/Services/settings";
import { PRODUCT_VERSION } from "../constants/product";
// NOTE: `createWorkspaceSnapshot` (workspace/core/serialization) is NOT imported here
// — that static edge closed 3 import cycles (logger/serialization/marketdata). It is
// injected via setWorkspaceSnapshotProvider(), wired once in main.jsx.

class ErrorReportService {
  constructor() {
    this.enabled = import.meta.env.VITE_ERROR_REPORTING_ENABLED !== "false";
    this.batchSize =
      parseInt(import.meta.env.VITE_ERROR_REPORTING_BATCH_SIZE) || 10;
    this.batchInterval =
      parseInt(import.meta.env.VITE_ERROR_REPORTING_BATCH_INTERVAL) || 30000; // 30 seconds
    this.maxQueueSize =
      parseInt(import.meta.env.VITE_ERROR_REPORTING_MAX_QUEUE_SIZE) || 100;

    this.queue = [];
    this.batchTimer = null;
    this.sending = false;
    this.retryCount = 0;
    this.maxRetries = 3;
    this.baseRetryDelay = 5000; // 5 seconds
    this.authProvider = null; // Set via setAuthProvider() after login
    this._snapshotProvider = null; // Set via setWorkspaceSnapshotProvider() in main.jsx

    // Load persisted queue from localStorage
    this.loadQueue();

    // Start batch timer
    this.startBatchTimer();

    // Send queued errors before page unload
    if (typeof window !== "undefined") {
      window.addEventListener("beforeunload", () => this.flush());
    }
  }

  /**
   * Inject the workspace-snapshot builder (createWorkspaceSnapshot from
   * workspace/core/serialization). Wired once in main.jsx so this module doesn't
   * import serialization directly (that static edge closed 3 import cycles).
   * @param {Function} fn - (chartsStore, opts) => snapshot
   */
  setWorkspaceSnapshotProvider(fn) {
    this._snapshotProvider = fn;
  }

  /**
   * Check if current page is TradingView (errors from TV should not be reported)
   */
  isOnTradingView() {
    if (typeof window === "undefined") return false;
    const host = window.location.host || "";
    return host.startsWith("tv.") || host.includes(".tv.");
  }

  /**
   * Report an error to the backend
   * @param {Error} error - Error object
   * @param {Object} errorInfo - Additional error info (e.g., React componentStack)
   * @param {Object} context - Application context
   */
  report(error, errorInfo = {}, context = {}) {
    if (!this.enabled) {
      console.log("[ErrorReport] Reporting disabled");
      return;
    }

    // Skip error reporting when on TradingView domain
    if (this.isOnTradingView()) {
      return;
    }

    try {
      const errorReport = this.createErrorReport(error, errorInfo, context);
      this.enqueue(errorReport);
    } catch (err) {
      console.error("[ErrorReport] Failed to create error report:", err);
    }
  }

  /**
   * Create error report payload
   */
  createErrorReport(error, errorInfo, context) {
    // Extract error details
    const message = error?.message || String(error) || "Unknown error";
    const stack = error?.stack || errorInfo?.componentStack || "";

    // Determine error type
    let errorType = context.errorType || "JavaScriptError";
    if (errorInfo?.componentStack) {
      errorType = "ReactError";
    }

    // Get app context
    const appContext = this.getAppContext(context);

    // Get browser context
    const browserContext = this.getBrowserContext();

    // Capture desktop state if appropriate
    const captureLevel = this.shouldCaptureDesktop(errorType, error);
    const desktopState = captureLevel
      ? this.captureDesktopState(captureLevel)
      : null;

    const report = {
      timestamp: new Date().toISOString(),
      errorType,
      message: this.sanitize(message),
      stack: this.sanitize(stack),
      component: context.component || errorInfo?.componentName || null,
      url: window.location.href, // Full URL including query params and hash
      userAgent: navigator.userAgent,
      appVersion: this.getAppVersion(),
      context: {
        ...appContext,
        ...browserContext,
        ...context.additionalContext,
        // Additional URL breakdown for easier analysis
        urlDetails: {
          protocol: window.location.protocol,
          host: window.location.host,
          pathname: window.location.pathname,
          search: window.location.search, // Query string with ?
          hash: window.location.hash, // Hash fragment with #
          origin: window.location.origin,
        },
        // Document referrer (previous page)
        referrer: document.referrer || null,
      },
    };

    // Add desktop state if captured
    if (desktopState) {
      report.context.desktopState = desktopState;
      report.context.desktopCaptureLevel = captureLevel;
    }

    return report;
  }

  /**
   * Get application context (symbol, indicators, connection, etc.)
   */
  getAppContext(context) {
    try {
      // Try to get state from stores
      let appContext = {};

      // Get chart state if available
      if (typeof window !== "undefined" && window.__CHART_STATE__) {
        const chartState = window.__CHART_STATE__;
        appContext = {
          symbol: chartState.symbol,
          chartType: chartState.chartType,
          indicatorsCount: chartState.indicators?.length || 0,
          drawingsCount: chartState.drawings?.length || 0,
          isConnected: chartState.isConnected,
        };
      }

      // Add marketdata connection ID for tracing reconnection issues
      if (
        typeof window !== "undefined" &&
        window.__MARKETDATA_CONNECTION_ID__ != null
      ) {
        appContext.connectionId = window.__MARKETDATA_CONNECTION_ID__;
      }

      // Override with provided context
      return { ...appContext, ...context };
    } catch (err) {
      console.warn("[ErrorReport] Failed to get app context:", err);
      return context;
    }
  }

  /**
   * Get browser context (memory, screen, etc.)
   */
  getBrowserContext() {
    try {
      const browserContext = {
        screenWidth: window.screen?.width,
        screenHeight: window.screen?.height,
        windowWidth: window.innerWidth,
        windowHeight: window.innerHeight,
        devicePixelRatio: window.devicePixelRatio,
      };

      // Add memory info if available (Chrome only)
      if (performance?.memory) {
        browserContext.memoryUsed = Math.round(
          performance.memory.usedJSHeapSize / 1024 / 1024,
        ); // MB
        browserContext.memoryLimit = Math.round(
          performance.memory.jsHeapSizeLimit / 1024 / 1024,
        ); // MB
      }

      return browserContext;
    } catch (err) {
      console.warn("[ErrorReport] Failed to get browser context:", err);
      return {};
    }
  }

  /**
   * Get app version from package.json (via Vite define config)
   */
  getAppVersion() {
    return PRODUCT_VERSION;
  }

  /**
   * Determine if desktop state should be captured for this error
   * @param {string} errorType - Type of error
   * @param {Error} error - Error object
   * @returns {string|null} - Capture level: 'lightweight', 'full', or null
   */
  shouldCaptureDesktop(errorType, error) {
    // Always capture full state for React errors (component crashes)
    if (errorType === "ReactError") {
      return "full";
    }

    // Capture full state for critical JavaScript errors
    if (errorType === "JavaScriptError") {
      // Check if it's a critical error by message patterns
      const message = error?.message || "";
      const isCritical =
        message.includes("Cannot read") ||
        message.includes("undefined is not") ||
        message.includes("null is not") ||
        message.includes("is not a function") ||
        message.includes("Maximum call stack");

      return isCritical ? "full" : "lightweight";
    }

    // Capture lightweight state for unhandled promises
    if (errorType === "UnhandledPromise") {
      return "lightweight";
    }

    // Capture lightweight state for network errors
    if (errorType === "NetworkError") {
      return "lightweight";
    }

    // Capture full state for state errors (likely data corruption)
    if (errorType === "StateError") {
      return "full";
    }

    // Don't capture for logged errors (too noisy)
    if (errorType === "LoggedError") {
      return null;
    }

    // Default: lightweight capture
    return "lightweight";
  }

  /**
   * Capture desktop state for error reproduction
   * @param {string} level - Capture level: 'lightweight' or 'full'
   * @returns {Object|null} - Desktop state snapshot or null
   */
  captureDesktopState(level) {
    try {
      // Get chart store state
      const chartsStore = this.getChartsStore();
      if (!chartsStore) {
        console.warn(
          "[ErrorReport] Chart store not available for desktop capture",
        );
        return null;
      }

      if (level === "lightweight") {
        // Lightweight capture: basic chart info without full serialization
        return {
          chartsCount: chartsStore.charts?.length || 0,
          selectedTab: chartsStore.selectedTab,
          symbols: chartsStore.charts?.map((c) => c.symbol) || [],
          indicatorCounts:
            chartsStore.charts?.map((c) => c.indicators?.length || 0) || [],
          interactiveObjectCounts:
            chartsStore.charts?.map((c) =>
              Object.values(c.interactiveObjects || {}).reduce(
                (sum, arr) => sum + (Array.isArray(arr) ? arr.length : 1),
                0,
              ),
            ) || [],
        };
      }

      if (level === "full") {
        // Full capture: complete workspace serialization via the INJECTED provider
        // (main.jsx wires createWorkspaceSnapshot). No provider → degrade gracefully:
        // still return the extra info below, just without the workspace snapshot (I2).
        const snapshot = this._snapshotProvider
          ? this._snapshotProvider(chartsStore, {
              simplified: true, // Use simplified to reduce size
              metadata: {
                captureReason: "Error reporting",
                captureTimestamp: new Date().toISOString(),
              },
            })
          : null;

        // Additional info not in standard snapshot
        return {
          ...(snapshot || {}),
          // Add active interactive tools
          activeInteractiveTools: chartsStore.activeInteractiveTools,
          // Add selection states
          selectionStates:
            chartsStore.charts?.map((c) => ({
              chartId: c.id,
              hasSelection: Object.values(c.interactiveObjects || {}).some(
                (arr) =>
                  Array.isArray(arr) && arr.some((item) => item.selected),
              ),
            })) || [],
        };
      }

      return null;
    } catch (err) {
      console.error("[ErrorReport] Failed to capture desktop state:", err);
      return null;
    }
  }

  /**
   * Get chart store instance
   * @returns {Object|null} - Chart store or null
   */
  getChartsStore() {
    try {
      // Try to get from global window object (exposed by store for debugging)
      if (typeof window !== "undefined" && window.__CHARTS_STORE__) {
        return window.__CHARTS_STORE__;
      }

      // Try to import dynamically
      // Note: This is async, so we can't use it directly in sync context
      // Instead, the store should be exposed on window for error reporting
      console.warn(
        "[ErrorReport] Chart store not found on window.__CHARTS_STORE__",
      );
      return null;
    } catch (err) {
      console.warn("[ErrorReport] Failed to get chart store:", err);
      return null;
    }
  }

  /**
   * Sanitize string to remove sensitive data
   */
  sanitize(text) {
    if (!text || typeof text !== "string") return text;

    // Remove JWT tokens
    text = text.replace(
      /eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g,
      "[JWT_REDACTED]",
    );

    // Remove passwords
    text = text.replace(
      /(password|pwd|pass|senha)[=:]\s*[^\s,}]+/gi,
      "$1=[REDACTED]",
    );

    // Truncate very long strings
    if (text.length > 10000) {
      text = text.substring(0, 10000) + "... [TRUNCATED]";
    }

    return text;
  }

  /**
   * Add error to queue
   */
  enqueue(errorReport) {
    // Check queue size
    if (this.queue.length >= this.maxQueueSize) {
      console.warn("[ErrorReport] Queue full, dropping oldest error");
      this.queue.shift();
    }

    this.queue.push(errorReport);
    this.saveQueue();

    console.log(
      `[ErrorReport] Queued error (${this.queue.length}/${this.batchSize})`,
    );

    // Send immediately if batch size reached
    if (this.queue.length >= this.batchSize) {
      this.flush();
    }
  }

  /**
   * Start batch timer
   */
  startBatchTimer() {
    if (this.batchTimer) {
      clearInterval(this.batchTimer);
    }

    this.batchTimer = setInterval(() => {
      if (this.queue.length > 0) {
        console.log("[ErrorReport] Batch interval reached, flushing queue");
        this.flush();
      }
    }, this.batchInterval);
  }

  /**
   * Enable urgent mode - flush errors every 2 seconds (used for auth errors)
   */
  setUrgentMode() {
    console.log(
      "[ErrorReport] Urgent mode enabled - switching to 2s batch interval",
    );
    this.batchInterval = 2000; // 2 seconds
    this.startBatchTimer();
    // Flush immediately if there are queued errors
    if (this.queue.length > 0) {
      this.flush();
    }
  }

  /**
   * Send all queued errors immediately
   */
  async flush() {
    if (!this.configReady) {
      console.log("[ErrorReport] Config not ready, deferring flush");
      return;
    }

    if (this.sending || this.queue.length === 0) {
      return;
    }

    this.sending = true;

    try {
      const batch = [...this.queue];
      this.queue = [];
      this.saveQueue();

      await this.sendBatch(batch);

      // Reset retry count on success
      this.retryCount = 0;

      console.log(`[ErrorReport] Successfully sent ${batch.length} error(s)`);
    } catch (err) {
      console.error("[ErrorReport] Failed to send batch:", err);

      // Put errors back in queue for retry
      this.queue = [...this.queue];
      this.saveQueue();

      // Schedule retry with exponential backoff
      this.scheduleRetry();
    } finally {
      this.sending = false;
    }
  }

  /**
   * Send batch to backend API
   */
  async sendBatch(batch) {
    // Get API URL from parsed URLs (properly split by semicolon)
    const broker = settings.marketdataUrl.broker || "TZ";
    const urls = parsedMarketdataUrls[broker];

    if (!urls || urls.length === 0) {
      throw new Error(`MarketData URL not configured for broker ${broker}`);
    }

    // Use the first available URL for error reporting
    const baseUrl = urls[0];

    const apiUrl = `${baseUrl}/api/errors/batch`;

    // Get auth token via provider (may be null before login → anonymous reporting)
    const authData = this.getAuthData();
    const isAnonymous = !authData?.token;

    if (isAnonymous) {
      console.log("[ErrorReport] No auth token - using anonymous reporting");
    }

    // Build headers
    const headers = {
      "Content-Type": "application/json",
    };

    // Add authorization header if token is available
    if (!isAnonymous) {
      headers["Authorization"] = `Bearer ${authData.token}`;
    }

    // Send batch
    const response = await fetch(apiUrl, {
      method: "POST",
      headers,
      body: JSON.stringify({
        errors: batch,
        anonymous: isAnonymous,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    const result = await response.json();

    if (result.failureCount > 0) {
      console.warn(
        `[ErrorReport] ${result.failureCount} error(s) failed to submit:`,
        result.errors,
      );
    }

    return result;
  }

  /**
   * Set auth provider callback (called by marketdata after login)
   * Avoids circular dependency: marketdata → logger → errorReportService → marketdata
   */
  setAuthProvider(provider) {
    this.authProvider = provider;
  }

  /**
   * Get authentication data via auth provider callback
   */
  getAuthData() {
    try {
      if (this.authProvider) {
        return this.authProvider();
      }
      return null;
    } catch (err) {
      console.warn("[ErrorReport] Failed to get auth data:", err);
      return null;
    }
  }

  /**
   * Schedule retry with exponential backoff
   */
  scheduleRetry() {
    if (this.retryCount >= this.maxRetries) {
      console.error("[ErrorReport] Max retries reached, giving up");
      return;
    }

    this.retryCount++;
    const delay = this.baseRetryDelay * Math.pow(2, this.retryCount - 1);

    console.log(
      `[ErrorReport] Scheduling retry ${this.retryCount}/${this.maxRetries} in ${delay}ms`,
    );

    setTimeout(() => {
      this.flush();
    }, delay);
  }

  /**
   * Save queue to localStorage
   */
  saveQueue() {
    try {
      localStorage.setItem("error-report-queue", JSON.stringify(this.queue));
    } catch (err) {
      console.warn("[ErrorReport] Failed to save queue:", err);
    }
  }

  /**
   * Load queue from localStorage
   */
  loadQueue() {
    try {
      const queueStr = localStorage.getItem("error-report-queue");
      if (queueStr) {
        this.queue = JSON.parse(queueStr);
        console.log(
          `[ErrorReport] Loaded ${this.queue.length} error(s) from queue`,
        );
      }
    } catch (err) {
      console.warn("[ErrorReport] Failed to load queue:", err);
      this.queue = [];
    }
  }

  /**
   * Clear queue
   */
  clearQueue() {
    this.queue = [];
    this.saveQueue();
    console.log("[ErrorReport] Queue cleared");
  }

  /**
   * Enable/disable error reporting
   */
  setEnabled(enabled) {
    this.enabled = enabled;
    console.log(`[ErrorReport] Reporting ${enabled ? "enabled" : "disabled"}`);
  }

  /**
   * Get current queue size
   */
  getQueueSize() {
    return this.queue.length;
  }

  /**
   * Mark configuration as ready - call this after runtime-config.json is loaded
   */
  markConfigReady() {
    console.log("[ErrorReport] Configuration ready, enabling error reporting");
    this.configReady = true;
    // If there are queued errors, flush them now
    if (this.queue.length > 0) {
      console.log(
        `[ErrorReport] Flushing ${this.queue.length} queued error(s)`,
      );
      this.flush();
    }
  }
}

// Create singleton instance
const errorReporter = new ErrorReportService();

// Export singleton and class
export default errorReporter;
export { ErrorReportService };
